/**
 * Microphone capture failure classification + user-facing copy.
 *
 * `room.localParticipant.setMicrophoneEnabled(true)` (livekit-client) ultimately
 * calls `getUserMedia`, which rejects with a `DOMException` whose `name` tells us
 * WHY. The previous handling mapped *every* failure to a single
 * "enable mic access in your browser" hint — actively misleading when the real
 * cause is a missing/stale device (`NotFoundError`) or a device held by another
 * app (`NotReadableError`) rather than a denied permission. We classify so the
 * surface can show honest, actionable copy, and so the hook knows which failures
 * are worth a default-device retry (a stale cached `deviceId` → `NotFoundError`)
 * versus which are not (`NotAllowedError` is a hard permission denial).
 */

export type MicErrorKind = 'not-found' | 'denied' | 'in-use' | 'unknown';

/** Map a getUserMedia / LiveKit capture rejection to a coarse, actionable kind. */
export function classifyMicError(err: unknown): MicErrorKind {
  const rawName = (err as { name?: unknown } | null | undefined)?.name;
  const name = typeof rawName === 'string' ? rawName : '';
  switch (name) {
    // No input device matched the request. The common real-world trigger is a
    // stale device selection (LiveKit re-requesting a cached `deviceId` that is
    // gone) — distinct from "no microphone exists on the machine". Either way a
    // default-device retry is the right next move.
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'not-found';
    // Permission denied — by the page prompt OR the OS (e.g. macOS Privacy).
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied';
    // A device was found but could not be opened — usually held by another app
    // or browser tab.
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'in-use';
    default:
      return 'unknown';
  }
}

/** Generic copy when Anaka never greets and the mic is not the identifiable cause. */
export const GREETING_UNREACHABLE_MESSAGE = "Couldn't reach Anaka — tap to retry";

/** Coarse mic-permission state (mirrors the Permissions API `PermissionState` + a fallback). */
export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * Choose the greeting-timeout error message. When Anaka never greets, a held or
 * un-started microphone is a common HIDDEN cause — the transport connects, the
 * agent looks unreachable, and the real problem (another app owns the mic) is
 * invisible. Surface honest, actionable copy instead of the generic line so the
 * user knows to free/allow the mic. Pure + total so it is unit-testable.
 */
export function greetingTimeoutMessage(input: {
  /** A mic capture failure was already classified this connect (soft hint shown). */
  micBlocked: boolean;
  micErrorKind: MicErrorKind | null;
  /** `room.localParticipant.isMicrophoneEnabled` — did the mic actually publish? */
  micEnabled: boolean;
  /** The user deliberately muted — an un-published mic is then expected, not a fault. */
  muted: boolean;
  /** Browser Permissions API read (`'unknown'` when unavailable, e.g. Safari). */
  micPermission: MicPermission;
}): string {
  // A classified mic failure (e.g. the reported device-in-use case, rejected as
  // NotReadableError) → its honest, actionable hint.
  if (input.micBlocked && input.micErrorKind) return micErrorHint(input.micErrorKind);
  // No classified error, but the mic never published and the user isn't muted:
  // the mic likely stalled. If the browser says permission is GRANTED, a mic that
  // still won't start is being held by another app/device → the in-use hint. If
  // permission is only 'prompt'/'unknown', it may be an unanswered dialog or a
  // genuinely-unreachable agent → stay generic (don't wrongly blame another app).
  if (!input.micEnabled && !input.muted && input.micPermission === 'granted') {
    return micErrorHint('in-use');
  }
  return GREETING_UNREACHABLE_MESSAGE;
}

/**
 * Best-effort read of the microphone permission state via the Permissions API,
 * degrading to `'unknown'` where it is unavailable or throws (Safari, older
 * browsers, or a query that rejects). Never throws.
 */
export async function readMicPermission(): Promise<MicPermission> {
  try {
    const perms = (globalThis as { navigator?: { permissions?: { query?: (d: { name: PermissionName }) => Promise<{ state: string }> } } })
      .navigator?.permissions;
    if (!perms?.query) return 'unknown';
    const status = await perms.query({ name: 'microphone' as PermissionName });
    if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
      return status.state;
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/** Short, actionable hint shown in the voice bar for each failure kind. */
export function micErrorHint(kind: MicErrorKind | null | undefined): string {
  switch (kind) {
    case 'not-found':
      return 'No microphone found — check it’s connected and enabled, then try again.';
    case 'denied':
      return 'Microphone blocked — allow mic access in your browser (and OS settings) to talk.';
    case 'in-use':
      return 'Microphone is in use by another app — close it, then try again.';
    default:
      // Generic fallback (also the copy when no kind is known) — keeps the
      // historical "Microphone blocked" wording so the surface degrades safely.
      return 'Microphone blocked — enable mic access in your browser to talk.';
  }
}
